import type {
  CashoutConflict,
  CashoutOffer,
} from "@/types/ApiResponses/Cashout";

// The shape of a rejected axios request, read structurally so this file needs
// no runtime import.
interface ErrorWithResponse {
  response?: { status?: number; data?: unknown };
}

const CONFLICT_STATUS = 409;
const OFFER_CHANGED = "CASHOUT_OFFER_CHANGED";

const isConflictBody = (data: unknown): data is CashoutConflict =>
  typeof data === "object" &&
  data !== null &&
  typeof (data as CashoutConflict).code === "string" &&
  "offer" in data;

/**
 * The 409 body of a refused cash out (code, tranKey, offer), or null for any
 * other failure (network, 5xx, validation).
 */
export const readCashoutConflict = (error: unknown): CashoutConflict | null => {
  const response = (error as ErrorWithResponse | null)?.response;
  if (response?.status !== CONFLICT_STATUS) return null;
  if (!isConflictBody(response.data)) return null;
  return response.data;
};

/**
 * The fresh offer of a CASHOUT_OFFER_CHANGED refusal (the price moved down
 * during the delay), to show the player and ask again. Never accepted on its
 * own. Null for every other refusal, or when the change left no offer.
 */
export const getChangedCashoutOffer = (error: unknown): CashoutOffer | null => {
  const conflict = readCashoutConflict(error);
  if (conflict?.code !== OFFER_CHANGED) return null;
  if (conflict.offer?.kind !== "offer") return null;
  return conflict.offer;
};
